import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getColleges,
  getUserTypes,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { baseURL, cloneDeep } from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import {
  IDescriptionData,
  IEditDescription,
} from "../../../../../models/master/descriptions";
import { setDescription } from "../../../../../store/slices/master/descriptionSlice";
import { updateDescription } from "../../../../../action/masters/descriptionActionCreator";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { pageBlockId } from "../../../../../constants/pageBlocks";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const DescriptionEdit = (props: IEditDescription) => {
  const dispatch = useDispatch();
  const {
    updateDescription,
    description,
    colleges,
    getColleges,
    getUserTypes,
    userTypes,
    pageBlocks,
    setShowEdit,
    getAllDescription,
    getUserPageBlocks,
  } = props;

  // states

  const [data, setData] = useState<IDescriptionData>(
    cloneDeep(description || {})
  );
  const [initialData, setInitialData] = useState<IDescriptionData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);
  const [hasChange, setHasChange] = useState(false);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value));
      _data.pageBlockId = 0;
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const fileType = selectedFile.type;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
        setImageName(selectedFile.name);
      }
    }
  };

  // Updated Joi schema for validation
  const schema = Joi.object().keys({
    id: Joi.number().optional().allow(null),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    description: Joi.string().required().label("Description is required"),
    pageBlockId: Joi.number().greater(0).required().label("Select Page Block"),
    collegeId: Joi.number().greater(0).required().label("Select University Management"),
  });

  const validationData = {
    ...data,
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("title", data.title);
      formData.append("image", data.image || "");
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("userType", data.userType?.toString() || "");
      formData.append("collegeId", data.collegeId?.toString() || "");
      formData.append("pageBlockId", data.pageBlockId?.toString() || "");
      formData.append("displayOrNot", data.displayOrNot ? "true" : "false");
      formData.append("file", data.file || "");
      setLoading(true);
      try {
        await updateDescription(formData, data.id ?? 0, setAlert);
      } finally {
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (description) {
      let _description = cloneDeep(description);
      setData(_description);
      setInitialData(_description);
      getUserPageBlocks(Number(_description.userType));
      getColleges(Number(_description.userType));
      if (_description.image) {
        const imageUrl = baseURL + _description.image;
        setImageName(_description.image);
        setImagePreview(imageUrl);
      }
    }
  }, [description]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllDescription();
      setShowEdit(false);
    }
  };

  useEffect(() => {
    getColleges();
    getUserTypes();
  }, []);

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    return () => {
      dispatch(setDescription(undefined));
      dispatch(setPageBlocks([]));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Edit Description
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-lg-12 form-group" id="collegewrap">
                <FloatingLabelDropDown
                  label={"University Management"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.collegeId}
                  name={"collegeId"}
                  optionsList={sortAlphabetically(colleges)}
                />
                {errors?.collegeId && (
                  <div className="text-danger">{errors.collegeId}</div>
                )}
              </div>

              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"User Type "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.userType}
                  name={"userType"}
                  optionsList={sortAlphabetically(
                    userTypes?.filter((type) => type.id !== UserTypes.Freshman)
                  )}
                />
                {errors?.userType && (
                  <div className="text-danger">{errors.userType}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"Page-Block "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.pageBlockId}
                  name={"pageBlockId"}
                  optionsList={sortAlphabetically(pageBlocks)}
                />
                {errors?.pageBlockId && (
                  <div className="text-danger">{errors.pageBlockId}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelTextInput
                  label={"Title "}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.title}
                  name={"title"}
                  maxLength={70}
                />
              </div>
              <div className="col-lg-12 form-group">
                {data.userType === UserTypes.Student &&
                  data?.pageBlockId == pageBlockId.studentCareerPath && (
                    <div className="d-flex gap-2">
                      <div className="w-100 inputfile-wrap">
                        <label className="profile_details_text">Image</label>
                        <div className="inputfile-flex">
                          {imagePreview && (
                            <div className="inputfile-img">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          )}
                          <input
                            type="file"
                            name="file"
                            id="image"
                            className="form-control d-none"
                            onChange={onFileChange}
                            accept="image/png, image/jpeg, image/jpg, image/svg, image/webp image/ico"
                          />
                          <div className="inputfile w-100">
                            <label htmlFor="image">Choose File</label>
                            <span>
                              {imageName
                                ? imageName.trim().split("/").pop()
                                : "No File Choosen"}
                            </span>
                          </div>
                        </div>
                      </div>
                      {errors?.file && (
                        <div className="text-danger">{errors.file}</div>
                      )}
                    </div>
                  )}
              </div>
              <div className="col-lg-12 form-group">
                <label className="profile_details_text">Description</label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
              <div className="col-lg-6 form-group">
                <label className="profile_details_text">Visibility</label>
                <div className="switches-container">
                  <input
                    type="radio"
                    id="switchShow"
                    name="visibilitySwitch"
                    value="true"
                    checked={data.displayOrNot === true}
                    onChange={() => setData({ ...data, displayOrNot: true })}
                  />
                  <label htmlFor="switchShow">Show</label>
                  <input
                    type="radio"
                    id="switchHide"
                    name="visibilitySwitch"
                    value="false"
                    checked={data.displayOrNot === false}
                    onChange={() => setData({ ...data, displayOrNot: false })}
                  />
                  <label htmlFor="switchHide">Hide</label>
                  <div className="switch-wrapper">
                    <div className="switch">
                      <div>Show</div>
                      <div>Hide</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={!hasChange}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateDescription: (data: any, id: number, callBack: Function) =>
    updateDescription(data, id, callBack),
  getColleges: () => getColleges(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number) => getUserPageBlocks(userType),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DescriptionEdit);
